import React, { useState, useEffect, useRef, useMemo } from 'react';
import useDebounce from '../../hooks/useDebounce';
import '../componentCss/webform.css';
import '../componentCss/sidebar.css';
import '../componentCss/userGroup.css';
import '../componentCss/healthcare-modern.css';
import { getUserGroupListAction, updateGroupsPermissionAction } from '../../Action/modernAdminActions';
import toast from 'react-hot-toast';
import "react-toastify/dist/ReactToastify.css";
import { Link } from 'react-router-dom';
import config from '../../config/config';
import DataTable from 'react-data-table-component';
import {
  Container,
  Row,
  Col,
  Modal,
  Form,
  Button,
  Card,
  Badge,
  Alert,
  ProgressBar,
  InputGroup,
  OverlayTrigger,
  Tooltip,
  Dropdown,
  ButtonGroup,
  Tab,
  Tabs
} from "react-bootstrap";
import {
  FaEye,
  FaShieldAlt,
  FaTimes,
  FaUsers,
  FaSave,
  FaUndo,
  FaCheckSquare,
  FaSquare
} from "react-icons/fa";
import {
  BsSearch,
  BsInfoCircle
} from "react-icons/bs";
import { MdWarning } from 'react-icons/md';
import ConfigurePermissionsModal from './ModalComponents/ConfigurePermissionsModal';
import ViewPermissionsModal from './ModalComponents/ViewPermissionsModal';

const RolesPermission = () => {
  const [userGroupList, setUserGroupList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [staffId, setStaffId] = useState(0);
  const [formList, setFormList] = useState([]);
  const [isModelOpen, setIsOpen] = useState(false);
  const [isLaoding, setisLaoding] = useState(0);
  const [staffIdPerson, setStaffIdPerson] = useState(0);
  const [isModelOpenPerson, setIsOpenPerson] = useState(false);
  const allDataRef = useRef();
  const singleRowRef = useRef();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 200);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configRoleId, setConfigRoleId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRoleId, setViewRoleId] = useState(null);

  // Enhanced state for better functionality
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [filterBy, setFilterBy] = useState('all'); // all, active, inactive
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    withPermissions: 0
  });
  const [permissionCategories, setPermissionCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // table, cards
  const [saveProgress, setSaveProgress] = useState(0);
  const [currentRoleName, setCurrentRoleName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [originalFormList, setOriginalFormList] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Enhanced filtering and helper functions
  const filteredFormList = formList.filter((data) => {
    const matchesSearch = (data.name && data.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (data.departmentName && data.departmentName.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory = selectedCategory === 'all' || data.departmentName === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Client-side filtering for immediate feedback while typing (before Apply is clicked)
  const filteredRoleList = useMemo(() => {
    // If no immediate search term or status filter, return server data as-is
    if (!searchTerm && filterBy === 'all') {
      return userGroupList;
    }

    // Client-side filtering for immediate feedback
    return userGroupList.filter((role) => {
      // Status filter
      let matchesStatus = true;
      if (filterBy === 'active') matchesStatus = role.is_active;
      else if (filterBy === 'inactive') matchesStatus = !role.is_active;

      // Search filter for immediate feedback (non-debounced for instant results)
      let matchesSearch = true;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();

        // Match against role name
        const roleName = (role.name || role.display_name || '').toLowerCase();

        // Match against role key/slug
        const roleKey = (role.key || role.slug || role.code || '').toLowerCase();

        // Match against joined permission names/keys
        let permissionText = '';
        if (role.permissions && Array.isArray(role.permissions)) {
          permissionText = role.permissions
            .map(p => `${p.name || ''} ${p.key || ''} ${p.permission_key || ''}`)
            .join(' ')
            .toLowerCase();
        }

        matchesSearch = roleName.includes(searchLower) ||
          roleKey.includes(searchLower) ||
          permissionText.includes(searchLower);
      }

      return matchesStatus && matchesSearch;
    });
  }, [userGroupList, filterBy, searchTerm]); // Note: using searchTerm instead of debouncedSearchTerm for instant feedback

  const ResetFilter = async () => {
    setSearchTerm('');
    setFilterBy('all');
    setCurrentPage(0);
    // Fetch fresh unfiltered data from server
    await getUserGroupListAPI();
  }

  // Helper functions for bulk operations
  const handleRoleSelection = (roleId) => {
    setSelectedRoles(prev => {
      const newSelection = prev.includes(roleId)
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId];
      setShowBulkActions(newSelection.length > 0);
      return newSelection;
    });
  };

  const selectAllRoles = () => {
    const allIds = filteredRoleList.map(role => role.role_id || role.id);
    setSelectedRoles(allIds);
    setShowBulkActions(true);
  };

  const clearSelection = () => {
    setSelectedRoles([]);
    setShowBulkActions(false);
  };

  // Bulk permission operations
  const applyBulkPermissions = async (action) => {
    if (selectedRoles.length === 0) return;

    try {
      setIsLoading(true);
      // Implementation for bulk operations would go here
      toast.success(`${action} applied to ${selectedRoles.length} roles`);
      clearSelection();
    } catch (error) {
      toast.error('Failed to apply bulk permissions');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate statistics
  const calculateStats = (roles) => {
    const stats = {
      total: roles.length,
      active: roles.filter(role => role.is_active).length,
      inactive: roles.filter(role => !role.is_active).length,
      withPermissions: roles.filter(role => role.permissions_count > 0).length
    };
    setStats(stats);
  };

  // Check for changes in permissions
  const detectPermissionChanges = (currentList, originalList) => {
    const hasChanges = JSON.stringify(currentList) !== JSON.stringify(originalList);
    setHasChanges(hasChanges);
    return hasChanges;
  };

  // Permission template operations
  const applyPermissionTemplate = (templateType) => {
    let newFormList = [...formList];

    switch (templateType) {
      case 'read_only':
        newFormList = formList.map(item => ({
          ...item,
          isAccess: 1,
          isEdit: 0,
          isDelete: 0,
          canAccess: true,
          canEdit: false,
          canDelete: false
        }));
        break;
      case 'full_access':
        newFormList = formList.map(item => ({
          ...item,
          isAccess: 1,
          isEdit: 1,
          isDelete: 1,
          canAccess: true,
          canEdit: true,
          canDelete: true
        }));
        break;
      case 'no_access':
        newFormList = formList.map(item => ({
          ...item,
          isAccess: 0,
          isEdit: 0,
          isDelete: 0,
          canAccess: false,
          canEdit: false,
          canDelete: false
        }));
        break;
      default:
        break;
    }

    setFormList(newFormList);
    detectPermissionChanges(newFormList, originalFormList);
  };
  const handlePageChange = (page) => {
    setCurrentPage(page - 1); // Convert 1-indexed to 0-indexed
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0); // Reset to first page when changing rows per page
  };
  const handleClosePerson = () => setIsOpenPerson(false);

  const handleOpen = (id) => {
    setViewRoleId(id);
    setShowViewModal(true);
  };

  // let submitPermissionForm = async (e) => {
  //   e.preventDefault();
  //   setIsLoading(1);

  //   try {
  //     const { default: rbacService } = await import('../../services/rbacService');

  //     // Optional: get actual admin user ID from context/localStorage
  //     const grantedBy = localStorage.getItem('admin_id') || 'admin_user_id';

  //     const permissionsToAssign = formList
  //       .filter(form => form.canAccess)
  //       .map(form => ({
  //         permission_id: form.id,
  //         granted: true,
  //         granted_by: grantedBy,
  //         conditions: {
  //           // You can send this only if your backend expects it
  //           department: form.departmentId,
  //           // Optionally: include time/data_scope here
  //         }
  //       }));

  //     const permissionsToRemove = formList
  //       .filter(form => !form.canAccess)
  //       .map(form => ({
  //         permission_id: form.id,
  //         granted: false,
  //         granted_by: grantedBy
  //       }));

  //     const updateData = {
  //       permissions_to_assign: permissionsToAssign,
  //       permissions_to_remove: permissionsToRemove
  //     };


  //     const res = await rbacService.assignPermissionsToRole(staffIdPerson, updateData);

  //     if (res.success) {
  //       toast.success("Role permissions updated successfully");
  //       setTimeout(() => {
  //         setIsLoading(0);
  //         setIsOpenPerson(false);
  //         getRolePermissionsAPI(staffIdPerson);
  //       }, 1500);
  //     } else {
  //       setIsLoading(0);
  //       toast.error(res.message || "Failed to update role permissions");
  //     }
  //   } catch (error) {
  //     setIsLoading(0);
  //     toast.error("An error occurred while updating permissions");
  //   }
  // };

  let submitPermissionForm = async (e) => {
    e.preventDefault();
    setisLaoding(1);

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setSaveProgress(prev => {
        if (prev < 90) return prev + 10;
        return prev;
      });
    }, 200);

    try {
      const { default: rbacService } = await import('../../services/rbacService');
      const permissions = formList.map((form) => ({
        permission_id: form.id,
        granted: !!form.isAccess,
        can_edit: !!form.isEdit,
        can_delete: !!form.isDelete,
        ...(form.departmentId
          ? {
            conditions: {
              departments: [form.departmentId],
            },
          }
          : {})
      }));


      const res = await rbacService.assignPermissionsToRole(staffIdPerson, permissions);

      clearInterval(progressInterval);
      setSaveProgress(100);

      if (res.status || res.success) {
        toast.success(
          <div>
            <strong>Success!</strong><br />
            Role permissions updated successfully
          </div>
        );

        // Reset form state
        setOriginalFormList(JSON.parse(JSON.stringify(formList)));
        setHasChanges(false);

        setTimeout(() => {
          setisLaoding(0);
          setSaveProgress(0);
          setIsOpenPerson(false);
        }, 1500);
      } else {
        setisLaoding(0);
        setSaveProgress(0);
        toast.error(
          <div>
            <strong>Error!</strong><br />
            {res.message || "Failed to update role permissions"}
          </div>
        );
      }
    } catch (error) {
      clearInterval(progressInterval);
      setisLaoding(0);
      setSaveProgress(0);

      // Handle RBAC/Permission errors (403) explicitly
      const status = error?.response?.status;
      if (status === 403) {
        toast.error(error?.response?.data?.detail || 'You do not have permission to update role permissions.');
        return;
      }

      toast.error(
        <div>
          <strong>Failed!</strong><br />
          An error occurred while updating permissions
        </div>
      );
    }
  };


  // let handleChange = (permissionType, formId) => {
  //   setFormList((prevForms) => {
  //     return prevForms.map((form) => {
  //       if (form.id === formId) {
  //         const updatedForm = { ...form };

  //         if (permissionType === "isAccess") {
  //           const newValue = form.isAccess === 1 ? 0 : 1;
  //           updatedForm.isAccess = newValue;
  //           updatedForm.canAccess = newValue === 1;
  //           // When access is granted/removed, also update edit and delete accordingly
  //           updatedForm.isEdit = newValue;
  //           updatedForm.canEdit = newValue === 1;
  //           updatedForm.isDelete = newValue;
  //           updatedForm.canDelete = newValue === 1;
  //         } else if (permissionType === "isEdit") {
  //           const newValue = form.isEdit === 1 ? 0 : 1;
  //           updatedForm.isEdit = newValue;
  //           updatedForm.canEdit = newValue === 1;
  //         } else if (permissionType === "isDelete") {
  //           const newValue = form.isDelete === 1 ? 0 : 1;
  //           updatedForm.isDelete = newValue;
  //           updatedForm.canDelete = newValue === 1;
  //         }

  //         return updatedForm;
  //       }
  //       return form;
  //     });
  //   });
  // };

  let handleChange = (permissionType, formId) => {
    setFormList((prevForms) => {
      const newForms = prevForms.map((form) => {
        if (form.id !== formId) return form;

        const updatedForm = { ...form };

        const toggle = (field) => {
          const newValue = updatedForm[field] === 1 ? 0 : 1;
          updatedForm[field] = newValue;
          updatedForm[`can${field.charAt(2).toUpperCase() + field.slice(3)}`] = newValue === 1;
        };

        switch (permissionType) {
          case "isAccess":
            toggle("isAccess");
            // cascade isEdit and isDelete with access
            updatedForm.isEdit = updatedForm.isAccess;
            updatedForm.canEdit = updatedForm.isAccess === 1;
            updatedForm.isDelete = updatedForm.isAccess;
            updatedForm.canDelete = updatedForm.isAccess === 1;
            break;
          case "isEdit":
            toggle("isEdit");
            // If enabling edit, also enable access
            if (updatedForm.isEdit === 1) {
              updatedForm.isAccess = 1;
              updatedForm.canAccess = true;
            }
            break;
          case "isDelete":
            toggle("isDelete");
            // If enabling delete, also enable access
            if (updatedForm.isDelete === 1) {
              updatedForm.isAccess = 1;
              updatedForm.canAccess = true;
            }
            break;
        }

        return updatedForm;
      });

      // Detect changes
      detectPermissionChanges(newForms, originalFormList);
      return newForms;
    });
  };

  const handleClose = () => setIsOpen(false);

  let getRolePermissionsAPI = async (roleId) => {
    try {
      // Validate roleId
      if (!roleId || roleId === 'undefined') {
        toast.error("Invalid role ID provided");
        setFormList([]);
        return;
      }

      setIsLoading(true);

      // Import rbacService dynamically
      const { default: rbacService } = await import('../../services/rbacService');

      // Get role details including permissions
      const roleRes = await rbacService.getRoleById(roleId);

      if (roleRes && (roleRes.data?.role_id || roleRes.role_id)) {
        const role = roleRes.data || roleRes;
        setCurrentRoleName(role.display_name || role.name);

        // Get all available permissions (increase page_size to fetch all)
        console.log('🔍 Fetching permissions with page_size: 1000');
        const permissionsRes = await rbacService.getPermissions({ page_size: 1000 });
        console.log('📊 Permissions response:', permissionsRes);

        if (permissionsRes && (permissionsRes.success ? permissionsRes.data : permissionsRes.permissions)) {
          const permissions = permissionsRes.success ? permissionsRes.data : permissionsRes.permissions;

          // Extract unique categories for filtering
          const categories = [...new Set(permissions.map(p => p.category || p.module || 'General'))];
          setPermissionCategories(categories);

          // Transform permissions to form format
          const transformedPermissions = permissions.map(permission => {
            // Check if this permission is assigned to the role
            const hasPermission = role.permissions && role.permissions.some(p =>
              p.permission_id === permission.id ||
              p.permission_id === permission.permission_id ||
              p.id === permission.id ||
              p.id === permission.permission_id
            );

            return {
              id: permission.id || permission.permission_id,
              name: permission.name || permission.display_name,
              description: permission.description || '',
              departmentName: permission.category || permission.module || 'General',
              departmentId: permission.module || permission.category || '',
              isAccess: hasPermission ? 1 : 0,
              isEdit: hasPermission ? 1 : 0,
              isDelete: hasPermission ? 1 : 0,
              canAccess: hasPermission,
              canEdit: hasPermission,
              canDelete: hasPermission,
              priority: permission.priority || 'medium'
            };
          });

          setFormList(transformedPermissions);
          setOriginalFormList(JSON.parse(JSON.stringify(transformedPermissions)));
          setHasChanges(false);
        } else {
          setFormList([]);
          toast.error("Failed to load available permissions");
        }
      } else {
        setFormList([]);
        toast.error("Failed to load role details");
      }
    } catch (error) {
      setFormList([]);
      toast.error("An error occurred while loading permissions");
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    // Check if a role was selected from the userGroup route
    const selectedRoleId = sessionStorage.getItem('selectedRoleForPermissions');
    if (selectedRoleId) {
      // Automatically open permission management for the selected role
      setTimeout(() => {
        handleOpenPerson(selectedRoleId);
        // Clear the session storage
        sessionStorage.removeItem('selectedRoleForPermissions');
      }, 500);
    }
    // Note: getUserGroupListAPI is called by the pagination useEffect below
  }, []);

  useEffect(() => {
    setSelectedRow(null);
  }, [currentPage]);

  // Fetch data when pagination state changes
  useEffect(() => {
    getUserGroupListAPI();
  }, [currentPage, rowsPerPage]);

  const getUserGroupListAPI = async () => {
    try {
      setIsLoading(true);

      // Prepare search parameters for backend
      const searchParams = {
        search: debouncedSearchTerm || searchTerm || undefined,
        status: filterBy !== 'all' ? filterBy : undefined,
        page: currentPage + 1,
        page_size: rowsPerPage
      };

      // Remove undefined values
      const cleanParams = Object.fromEntries(
        Object.entries(searchParams).filter(([_, value]) => value !== undefined)
      );

      let res = await getUserGroupListAction(cleanParams);
      console.log('📊 Backend Response:', {
        success: res.success,
        itemsLength: res.items?.length,
        dataLength: res.data?.length,
        total: res.total,
        page: res.page,
        pageSize: res.pageSize
      });

      if (res.success) {
        // Backend returns 'items' array, not 'data'
        const roles = res.items || res.data || [];
        console.log(`✅ Setting ${roles.length} roles, total count: ${res.total || 0}`);
        setUserGroupList(roles);
        setTotalRows(res.total || 0);
        calculateStats(roles);
      } else {
        setUserGroupList([]);
        setTotalRows(0);
        toast.error(res.message || "Failed to load roles");
      }
    } catch (error) {
      setUserGroupList([]);
      toast.error("An error occurred while loading roles");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenPerson = (id) => {
    setStaffIdPerson(id);
    getRolePermissionsAPI(id);
    setIsOpenPerson(true);
  };

  const openConfigModal = (roleId) => {
    setConfigRoleId(roleId);
    setShowConfigModal(true);
  };

  const columns = [
    {
      name: "#",
      cell: (row, index, column, id) => {
        return index + 1 + currentPage * rowsPerPage;
      },
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: "Name",
      selector: (row) => row.name || row.display_name,
      sortable: true,
      width: "15%"
    },
    {
      name: "Description",
      selector: (row) => row.description || 'No description provided',
      sortable: true,
      width: "31%"
    },
    {
      name: 'Status',
      cell: (row) => (
        <div>
          <span className={`healthcare-status-badge ${row.is_active ? 'healthcare-status-active' : 'healthcare-status-inactive'}`}>
            {row.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      ),
      sortable: true,
      width: '15%',
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="d-flex gap-2">
          <button
            onClick={() => handleOpen(row.role_id || row.id)}
            title="View Permissions"
            className="healthcare-action-btn healthcare-action-view"
          >
            <FaEye size={14} />
            View Permissions
          </button>
          <button
            onClick={() => openConfigModal(row.role_id || row.id)}
            title="Configure Permissions"
            className="healthcare-action-btn healthcare-action-edit"
          >
            <FaShieldAlt size={14} />
            Configure Permissions
          </button>
        </div>
      ),
      sortable: true,
      width: "30%"
    },
  ];




  return (
    <>
      <div className="healthcare-container">
        <Container fluid className="p-4">
          <Row>
            <Col lg={12} md={12} sm={12} className="">
              <div className="healthcare-card">
                <div className="healthcare-header">
                  <h5>
                    <FaShieldAlt size={24} />
                    Role Permissions Management
                  </h5>
                  <div className="subtitle">
                    Configure access permissions for each role in the system
                  </div>
                </div>
                <div className="p-4">
                  <div className="healthcare-table-header">
                    <div className="healthcare-table-title">
                      <FaShieldAlt />
                      Role Permissions Management
                    </div>
                    <div className="healthcare-table-subtitle">
                      Assign roles and configure permissions for system roles
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>


          <Row>
            <Col lg={12} md={12} sm={12} className="mt-3">
              <div className="healthcare-card mt-4">
                <div className="p-4">
                  <div className="healthcare-table-header">
                    <div className="d-flex align-items-center gap-3 flex-grow-1">
                      <div className="healthcare-search-container position-relative">
                        <input
                          type="text"
                          className="healthcare-search-input"
                          placeholder="Search roles by name, key, permissions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{ paddingRight: searchTerm ? '35px' : '12px' }}
                        />
                        {searchTerm && (
                          <button
                            className="btn btn-sm position-absolute"
                            style={{
                              right: '8px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              border: 'none',
                              background: 'transparent',
                              color: '#6c757d',
                              padding: '2px 4px'
                            }}
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                          >
                            <FaTimes size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="healthcare-table-actions">
                      <div className="d-flex gap-2">
                        <select
                          className="healthcare-select"
                          value={filterBy}
                          onChange={(e) => setFilterBy(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active Only</option>
                          <option value="inactive">Inactive Only</option>
                        </select>
                        <button
                          className="healthcare-btn healthcare-btn-secondary"
                          onClick={ResetFilter}
                        >
                          Reset
                        </button>
                        <button
                          className="healthcare-btn healthcare-btn-primary"
                          onClick={getUserGroupListAPI}
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <br />

              <div className="healthcare-table-container mt-4">
                {isLoading ? (
                  <div className="healthcare-loading">
                    <div className="healthcare-loading-spinner"></div>
                    <div className="healthcare-loading-text">Loading roles...</div>
                  </div>
                ) : (
                  <>
                    {console.log('🎨 Rendering DataTable with:', {
                      userGroupListLength: userGroupList.length,
                      totalRows: totalRows,
                      currentPage: currentPage,
                      rowsPerPage: rowsPerPage,
                      userGroupListIds: userGroupList.map(r => r.role_id || r.id)
                    })}

                    <DataTable
                      key={`roles-table-${rowsPerPage}`}
                      columns={columns}
                      data={userGroupList}
                      pagination
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationPerPage={rowsPerPage}
                      paginationDefaultPage={currentPage + 1}
                      paginationRowsPerPageOptions={[10, 15, 25, 50, 100]}
                      paginationComponentOptions={{
                        rowsPerPageText: 'Rows per page:',
                        rangeSeparatorText: 'of',
                      }}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handleRowsPerPageChange}
                      noDataComponent={
                        <div className="healthcare-loading">
                          <FaUsers size={48} className="text-muted mb-3" />
                          <div className="healthcare-loading-text text-muted">
                            {debouncedSearchTerm ? `No roles found matching "${debouncedSearchTerm}"` : 'No roles found'}
                          </div>
                          {debouncedSearchTerm && (
                            <button
                              className="btn btn-link btn-sm text-primary mt-2"
                              onClick={() => setSearchTerm('')}
                            >
                              Clear search to see all roles
                            </button>
                          )}
                        </div>
                      }
                    />
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/*------------------------------- Section For Showing Single Print--------------------------------------------------- */}
      <div style={{ display: 'none' }}>
        <div className="container mt-5" ref={singleRowRef}>
          {selectedRow && (
            <div className="card shadow-sm">
              <div className="card-body">
                <h2 className="card-title text-center mb-4">Role Details</h2>
                <div className="list-group">
                  <div className="list-group-item">
                    <strong>ID:</strong> {selectedRow.id}
                  </div>
                  <div className="list-group-item">
                    <strong>Name:</strong> {selectedRow.name}
                  </div>
                  <div className="list-group-item">
                    <strong>Description:</strong> {selectedRow.description}
                  </div>
                  <div className="list-group-item">
                    <strong>Code:</strong> {selectedRow.code}
                  </div>
                  <div className="list-group-item">
                    <strong>Status:</strong> {selectedRow.status == 1 ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/*------------------------------- Section For Showing All Data For Print--------------------------------------------------- */}

      {/* New Configure Permissions Modal */}
      <ConfigurePermissionsModal
        show={showConfigModal}
        roleId={configRoleId}
        onHide={() => setShowConfigModal(false)}
        onSaved={() => {
          setShowConfigModal(false);
          // Optionally refresh the role list or other state here
        }}
      />

      {/* New View Permissions Modal */}
      <ViewPermissionsModal
        show={showViewModal}
        roleId={viewRoleId}
        onHide={() => setShowViewModal(false)}
      />

      <Modal
        show={isModelOpen}
        onHide={() => handleClose(false)}
        dialogClassName="modal-90w modal-right"
        aria-labelledby="example-custom-modal-styling-title"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <b>Permissions</b>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <Form>
            <Row className="mb-4">
              <div className="col-md-3">
                <Form.Label>
                  <b> Form Name </b>
                </Form.Label>{" "}
                <br />
              </div>


              <div className="col-md-3">
                <Form.Label>
                  <b> Department Name </b>
                </Form.Label>{" "}
                <br />
              </div>

              <div className="col-md-3">
                <Form.Label>
                  <b>Access</b>
                </Form.Label>
              </div>

              <div className="col-md-3">
                <Form.Label>
                  <b> Edit </b>
                </Form.Label>
              </div>

              {/* <div className="col-md-2">
                <Form.Label>
                  {" "}
                  <b> Delete </b>
                </Form.Label>
              </div> */}
            </Row>
            <hr />

            {formList.length > 0 ? (
              formList.some(data => data.isAccess === 1 || data.isEdit === 1 || data.isDelete === 1) ? (
                formList.map((data) => (
                  <Row className="mb-4" key={data.id} style={{ display: data.isAccess === 1 || data.isEdit === 1 || data.isDelete === 1 ? 'flex' : 'none' }}>
                    <div className="col-md-3">{data.name}</div>
                    {/* <div className="col-md-3">{data.departmentName?.length > 10 ? `${data.departmentName.slice(0, 7)}..` : data.departmentName}</div> */}
                    <div className="col-md-3 text-wrap" title={data.departmentName}>
                      {data.departmentName?.length > 10 ? `${data.departmentName.slice(0, 7)}..` : data.departmentName}
                    </div>
                    <div className="col-md-3">
                      <div>
                        {data.isAccess === 1 ? <p style={{ color: "green" }}>Yes</p> : <p style={{ color: "red" }}>No</p>}
                      </div>
                    </div>

                    <div className="col-md-3">
                      <div>
                        {data.isEdit === 1 ? <p style={{ color: "green" }}>Yes</p> : <p style={{ color: "red" }}>No</p>}
                      </div>
                    </div>

                    {/* <div className="col-md-2">
                      <div>
                        {data.isDelete === 1 ? <p style={{ color: "green" }}>Yes</p> : <p style={{ color: "red" }}>No</p>}
                      </div>
                    </div> */}
                  </Row>
                ))
              ) : (
                <Row className="mb-4">
                  <div className="col-md-12" style={{ textAlign: "center" }}>
                    No Data Found
                  </div>
                </Row>
              )
            ) : ""}


          </Form>
        </Modal.Body>
      </Modal>



      {/* Enhanced Permissions Modal */}
      <Modal
        show={isModelOpenPerson}
        onHide={() => handleClosePerson(false)}
        size="xl"
        fullscreen="lg-down"
        aria-labelledby="permissions-modal-title"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title id="permissions-modal-title">
            <FaShieldAlt className="me-2" />
            Configure Permissions - {currentRoleName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {isLaoding && (
            <div className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 1000 }}>
              <div className="text-center">
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Saving...</span>
                </div>
                <div className="mb-2">
                  <small className="text-muted">Saving permissions... {saveProgress}%</small>
                </div>
                <ProgressBar now={saveProgress} variant="primary" style={{ height: '4px', width: '200px' }} />
              </div>
            </div>
          )}

          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="border-bottom">
            <Tab eventKey="overview" title={<><BsInfoCircle className="me-2" />Overview</>}>
              <div className="p-4">
                <Alert variant="info">
                  <BsInfoCircle className="me-2" />
                  <strong>Permission Management</strong><br />
                  Configure what this role can access in the system. Changes are automatically saved when you submit.
                </Alert>

                {hasChanges && (
                  <Alert variant="warning">
                    <MdWarning className="me-2" />
                    <strong>Unsaved Changes</strong><br />
                    You have made changes that haven't been saved yet.
                  </Alert>
                )}

                <Row className="mb-4">
                  <Col md={6}>
                    <Card className="border-0 bg-light">
                      <Card.Body className="text-center">
                        <h5 className="text-primary">{filteredFormList.filter(f => f.isAccess === 1).length}</h5>
                        <small className="text-muted">Permissions Granted</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="border-0 bg-light">
                      <Card.Body className="text-center">
                        <h5 className="text-secondary">{filteredFormList.filter(f => f.isAccess === 0).length}</h5>
                        <small className="text-muted">Permissions Denied</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <div className="mb-3">
                  <h6>Quick Templates</h6>
                  <ButtonGroup size="sm">
                    <Button variant="outline-success" onClick={() => applyPermissionTemplate('full_access')}>
                      <FaCheckSquare className="me-1" />
                      Full Access
                    </Button>
                    <Button variant="outline-warning" onClick={() => applyPermissionTemplate('read_only')}>
                      <FaEye className="me-1" />
                      Read Only
                    </Button>
                    <Button variant="outline-danger" onClick={() => applyPermissionTemplate('no_access')}>
                      <FaSquare className="me-1" />
                      No Access
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            </Tab>

            <Tab eventKey="permissions" title={<><FaShieldAlt className="me-2" />Permissions</>}>
              <div className="p-4">
                {/* Search and Filter */}
                <Row className="mb-4">
                  <Col md={8}>
                    <InputGroup>
                      <InputGroup.Text>
                        <BsSearch />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search permissions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md={4}>
                    <Form.Select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                      <option value="all">All Categories</option>
                      {permissionCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </Row>

                {/* Permissions Header */}
                <Card className="border-0 bg-light mb-3">
                  <Card.Body className="py-2">
                    <Row className="align-items-center">
                      <Col md={4}>
                        <strong>Permission Name</strong>
                      </Col>
                      <Col md={3}>
                        <strong>Category</strong>
                      </Col>
                      <Col md={2} className="text-center">
                        <strong>Access</strong>
                      </Col>
                      <Col md={2} className="text-center">
                        <strong>Edit</strong>
                      </Col>
                      <Col md={1} className="text-center">
                        <strong>Delete</strong>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* Permissions List */}
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {filteredFormList.length > 0 ? (
                    filteredFormList.map((data) => (
                      <Card key={data.id} className="border-0 border-bottom mb-2">
                        <Card.Body className="py-3">
                          <Row className="align-items-center">
                            <Col md={4}>
                              <div>
                                <div className="fw-medium">{data.name}</div>
                                {data.description && (
                                  <small className="text-muted">{data.description}</small>
                                )}
                              </div>
                            </Col>
                            <Col md={3}>
                              <Badge bg="secondary" className="px-2 py-1">
                                {data.departmentName}
                              </Badge>
                            </Col>
                            <Col md={2} className="text-center">
                              <Form.Check
                                type="switch"
                                id={`access-${data.id}`}
                                checked={data.isAccess === 1}
                                onChange={() => handleChange("isAccess", data.id)}
                                label=""
                              />
                            </Col>
                            <Col md={2} className="text-center">
                              <Form.Check
                                type="switch"
                                id={`edit-${data.id}`}
                                checked={data.isEdit === 1}
                                onChange={() => handleChange("isEdit", data.id)}
                                label=""
                                disabled={data.isAccess === 0}
                              />
                            </Col>
                            <Col md={1} className="text-center">
                              <Form.Check
                                type="switch"
                                id={`delete-${data.id}`}
                                checked={data.isDelete === 1}
                                onChange={() => handleChange("isDelete", data.id)}
                                label=""
                                disabled={data.isAccess === 0}
                              />
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <FaShieldAlt size={48} className="text-muted mb-3" />
                      <h6 className="text-muted">No permissions found</h6>
                      <p className="text-muted">Try adjusting your search criteria</p>
                    </div>
                  )}
                </div>
              </div>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>
              {hasChanges && (
                <Alert variant="warning" className="p-2 mb-0 d-flex align-items-center">
                  <MdWarning className="me-2" />
                  <small>You have unsaved changes</small>
                </Alert>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={() => setFormList([...originalFormList])}>
                <FaUndo className="me-1" />
                Reset
              </Button>
              <Button variant="secondary" onClick={() => handleClosePerson(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitPermissionForm}
                disabled={isLaoding || !hasChanges}
              >
                {isLaoding ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="me-1" />
                    Save Permissions
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default RolesPermission;